import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import Avatar from '@/components/ui/Avatar';

// ─────────────────────────────────────────────────────────────────────────────
// Nav links
// ─────────────────────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { to: '/',          label: 'Feed'      },
  { to: '/categories', label: 'Categories' },
  { to: '/tags',      label: 'Tags'      },
  { to: '/search',    label: 'Search'    },
  { to: '/about',     label: 'About'     },
];

// ─────────────────────────────────────────────────────────────────────────────
// Search bar (expand/collapse)
// ─────────────────────────────────────────────────────────────────────────────

function SearchBar({ onClose }: { onClose: () => void }) {
  const ref      = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => { ref.current?.focus(); }, []);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = ref.current?.value.trim();
    if (q) { navigate(`/search?q=${encodeURIComponent(q)}`); onClose(); }
  };

  return (
    <motion.form
      key="search"
      initial={{ opacity: 0, width: 0 }}
      animate={{ opacity: 1, width: '240px' }}
      exit={{ opacity: 0, width: 0 }}
      transition={{ duration: 0.2 }}
      onSubmit={submit}
      className="overflow-hidden"
    >
      <div className="relative">
        <input
          ref={ref}
          type="search"
          placeholder="search --all"
          className="w-full rounded border border-surface-border bg-bg-900 px-3 py-1.5 pr-8 font-mono text-xs text-text-primary placeholder:text-text-muted focus:border-terminal-green focus:outline-none focus:ring-1 focus:ring-terminal-green"
        />
        <button
          type="button"
          onClick={onClose}
          className="absolute inset-y-0 right-2 flex items-center text-text-muted hover:text-text-primary"
          aria-label="Close search"
        >
          ×
        </button>
      </div>
    </motion.form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// UserMenu dropdown
// ─────────────────────────────────────────────────────────────────────────────

function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen]  = useState(false);
  const ref              = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  if (!user) {
    return (
      <div className="flex items-center gap-2">
        <Link
          to="/login"
          className="font-mono text-xs text-text-muted hover:text-terminal-green transition-colors"
        >
          login
        </Link>
        <Link
          to="/register"
          className="rounded border border-red-500 bg-red-500/10 px-3 py-1 font-mono text-xs text-red-400 hover:bg-red-500/20 transition-colors"
        >
          register
        </Link>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 rounded border border-transparent px-2 py-1 hover:border-surface-border transition-colors"
        aria-expanded={open}
        aria-label="User menu"
      >
        <Avatar username={user.username} src={user.avatarUrl} size="sm" />
        <span className="hidden font-mono text-xs text-text-secondary sm:block">
          {user.username}
        </span>
        <svg className={`h-3 w-3 text-text-muted transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0,  scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={{ duration: 0.12 }}
            className="absolute right-0 top-full z-50 mt-1 min-w-[160px] rounded-lg border border-surface-border bg-surface shadow-xl"
          >
            <div className="border-b border-surface-border px-3 py-2">
              <p className="font-mono text-xs text-text-muted">{user.email}</p>
              <p className="font-mono text-[10px] text-text-muted/60 uppercase tracking-wider mt-0.5">{user.role}</p>
            </div>
            <div className="py-1">
              <MenuItem to={`/u/${user.username}`}>Profile</MenuItem>
              {(user.role === 'ADMIN' || user.role === 'EDITOR') && (
                <MenuItem to="/admin">Admin panel</MenuItem>
              )}
              <button
                type="button"
                onClick={() => { logout(); setOpen(false); }}
                className="block w-full px-3 py-1.5 text-left font-mono text-xs text-red-400 hover:bg-red-500/10 transition-colors"
              >
                logout
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function MenuItem({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="block px-3 py-1.5 font-mono text-xs text-text-secondary hover:bg-surface-elevated hover:text-terminal-green transition-colors"
    >
      {children}
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mobile overlay
// ─────────────────────────────────────────────────────────────────────────────

function MobileMenu({
  open,
  onClose,
}: {
  open:    boolean;
  onClose: () => void;
}) {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();

  const submitSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = (e.currentTarget.elements.namedItem('q') as HTMLInputElement).value.trim();
    if (q) { navigate(`/search?q=${encodeURIComponent(q)}`); onClose(); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="mobile-menu"
          initial={{ opacity: 0, x: '100%' }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: '100%' }}
          transition={{ type: 'tween', duration: 0.22 }}
          className="fixed inset-0 z-[60] flex flex-col bg-bg-950"
        >
          <div className="flex items-center justify-between border-b border-surface-border px-4 py-3">
            <Logo />
            <button
              type="button"
              onClick={onClose}
              className="font-mono text-text-muted hover:text-text-primary"
              aria-label="Close menu"
            >
              ✕
            </button>
          </div>

          <div className="flex flex-col gap-1 p-4">
            <form onSubmit={submitSearch} className="mb-4">
              <input
                name="q"
                type="search"
                placeholder="search --all"
                className="w-full rounded border border-surface-border bg-bg-900 px-3 py-2 font-mono text-sm text-text-primary placeholder:text-text-muted focus:border-terminal-green focus:outline-none"
              />
            </form>

            {NAV_LINKS.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                onClick={onClose}
                className={({ isActive }) =>
                  `rounded px-3 py-2.5 font-mono text-sm transition-colors ${
                    isActive ? 'bg-surface text-terminal-green' : 'text-text-secondary hover:text-terminal-green'
                  }`
                }
                end={to === '/'}
              >
                <span className="text-text-muted mr-2">$</span>{label}
              </NavLink>
            ))}
          </div>

          <div className="mt-auto border-t border-surface-border p-4">
            {user ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <Avatar username={user.username} src={user.avatarUrl} size="md" />
                  <div>
                    <p className="font-mono text-sm text-text-primary">{user.username}</p>
                    <p className="font-mono text-xs text-text-muted">{user.role}</p>
                  </div>
                </div>
                {(user.role === 'ADMIN' || user.role === 'EDITOR') && (
                  <Link
                    to="/admin"
                    onClick={onClose}
                    className="rounded border border-surface-border px-3 py-2 text-center font-mono text-xs text-text-secondary hover:border-terminal-green hover:text-terminal-green transition-colors"
                  >
                    Admin panel
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => { logout(); onClose(); }}
                  className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 font-mono text-xs text-red-400 hover:bg-red-500/20 transition-colors"
                >
                  logout
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Link
                  to="/login"
                  onClick={onClose}
                  className="rounded border border-surface-border px-3 py-2 text-center font-mono text-sm text-text-secondary hover:border-terminal-green hover:text-terminal-green transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  onClick={onClose}
                  className="rounded border border-red-500 bg-red-500/10 px-3 py-2 text-center font-mono text-sm text-red-400 hover:bg-red-500/20 transition-colors"
                >
                  Register
                </Link>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Logo
// ─────────────────────────────────────────────────────────────────────────────

function Logo() {
  return (
    <Link to="/" className="group flex items-center gap-0">
      <span className="font-mono text-lg font-bold tracking-tight text-text-primary group-hover:text-terminal-green transition-colors">
        PROMPT
      </span>
      <span className="font-mono text-lg font-bold text-red-400">//</span>
      <span className="font-mono text-lg font-bold tracking-tight text-text-primary group-hover:text-terminal-green transition-colors">
        HIJACK
      </span>
      <span className="ml-0.5 inline-block h-4 w-0.5 animate-cursor-blink bg-terminal-green" />
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Header
// ─────────────────────────────────────────────────────────────────────────────

export default function Header() {
  const [searchOpen,  setSearchOpen]  = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);

  // Close mobile menu on resize
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 768) setMobileOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <>
      {/* Skip to main content — keyboard accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:border focus:border-terminal-green focus:bg-bg-950 focus:px-4 focus:py-2 focus:font-mono focus:text-sm focus:text-terminal-green focus:outline-none"
      >
        Skip to main content
      </a>

      <header
        className="sticky top-7 z-30 border-top-red bg-bg-950/90 backdrop-blur-md"
        style={{ borderBottom: '1px solid #2e3348' }}
      >
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-4 px-4 lg:px-6">
          {/* Left: logo */}
          <Logo />

          {/* Center: nav (desktop) */}
          <nav className="hidden items-center gap-1 md:flex">
            {NAV_LINKS.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  `rounded px-3 py-1.5 font-mono text-xs transition-colors ${
                    isActive
                      ? 'bg-surface text-terminal-green'
                      : 'text-text-muted hover:text-text-primary'
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>

          {/* Right: search + user */}
          <div className="flex items-center gap-2">
            <AnimatePresence mode="wait">
              {searchOpen ? (
                <SearchBar key="open" onClose={() => setSearchOpen(false)} />
              ) : (
                <motion.button
                  key="icon"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  className="rounded p-1.5 text-text-muted hover:text-terminal-green transition-colors"
                  aria-label="Open search"
                >
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </motion.button>
              )}
            </AnimatePresence>

            <div className="hidden md:block">
              <UserMenu />
            </div>

            {/* Hamburger (mobile) */}
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="rounded p-1.5 text-text-muted hover:text-text-primary transition-colors md:hidden"
              aria-label="Open menu"
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </>
  );
}
